import dayjs from 'dayjs';
import { BasicDatePicker, RadioOption, Typography } from '../../atoms';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';

export interface RadioWithOptionalDatePickerType {
    isYYYY?: boolean;
    isMMYYYY?: boolean;
    options: Array<Record<string, any>> | any; // any to support old plans
    field: Record<string, any>;
    enabled?: boolean;
    hasError?: boolean;
    showCustomDateField?: boolean;
    selectedKey?: string; // Optional: for external control of which specific option is selected
    // eslint-disable-next-line no-unused-vars
    onSelectedKeyChange?: (selectedKey: string) => void; // Optional: callback when selected key changes
}

export const RadioWithOptionalDatePicker = ({
    field,
    options,
    isYYYY,
    isMMYYYY,
    enabled = true,
    hasError = false,
    showCustomDateField = true,
    selectedKey,
    onSelectedKeyChange
}: RadioWithOptionalDatePickerType) => {
    const { t } = useTranslation();
    // Helper function to generate key based on index
    const generateOptionKey = (index: number): string => {
        return `option_${index}`;
    };

    const [displayedOptions, setDisplayedOptions] = useState<RadioWithOptionalDatePickerType['options']>([]);
    const [internalSelectedKey, setInternalSelectedKey] = useState<string>(selectedKey || '');
    const [userHasInteracted, setUserHasInteracted] = useState<boolean>(false);
    useEffect(() => {
        const optionsWithKeys = options.filter(Boolean).map((option: any, index: number) => ({
            ...option,
            __optionKey: generateOptionKey(index) // Use index-based key
        }));

        // Only add custom date option if showCustomDateField is true
        const optionsToDisplay = showCustomDateField
            ? [
                  ...optionsWithKeys,
                  {
                      label: <Typography variant="labels.largeDemibold" label={t('CUSTOM_DATE_LABEL')} />,
                      value: field.value && !isCustomDateInOptions(field.value) ? field.value : '',
                      disabled: !enabled,
                      __optionKey: generateOptionKey(optionsWithKeys.length) // Custom date gets next index
                  }
              ]
            : optionsWithKeys;

        setDisplayedOptions(optionsToDisplay);

        // Initialize selected key based on selectedKey prop or field.value
        if (selectedKey) {
            // Use external selectedKey if provided
            setInternalSelectedKey(selectedKey);
        } else if (field.value && !userHasInteracted) {
            // Only auto-select first matching option if user hasn't interacted yet and no external selectedKey
            // Find the first option with matching value
            const matchingOption = optionsWithKeys.find((opt: any) => opt.value === field.value);
            if (matchingOption) {
                setInternalSelectedKey(matchingOption.__optionKey);
            } else if (!isCustomDateInOptions(field.value)) {
                // If field.value doesn't match any option, show custom date as selected
                setInternalSelectedKey(generateOptionKey(optionsWithKeys.length));
            }
        } else if (!field.value && !userHasInteracted && !selectedKey) {
            // For null field.value, no option is selected (only if user hasn't interacted and no external selectedKey)
            setInternalSelectedKey('');
        }
    }, [options, field.value, selectedKey, showCustomDateField]);
    // Function to append options with Date Picker component
    const addCustomDateToOptions: RadioWithOptionalDatePickerType['options'] = (value: string) => {
        const filteredOptions = options.filter(Boolean).map((option: any, index: number) => ({
            ...option,
            __optionKey: generateOptionKey(index) // Use index-based key
        }));

        // Only add custom date option if showCustomDateField is true
        if (!showCustomDateField) {
            return filteredOptions;
        }

        return [
            ...filteredOptions,
            {
                label: <Typography variant="labels.largeDemibold" label={t('CUSTOM_DATE_LABEL')} />,
                value: value || displayedOptions?.[displayedOptions?.length - 1]?.value,
                disabled: !enabled,
                __optionKey: generateOptionKey(filteredOptions.length) // Custom date gets next index
            }
        ];
    };
    // check if selected custom date matches with any other option values
    const isCustomDateInOptions = (val: string) => options.filter(Boolean).find((opt: any) => opt && opt.value === val);

    useEffect(() => {
        if (showCustomDateField && !isCustomDateInOptions(field.value)) {
            setDisplayedOptions(addCustomDateToOptions(field.value || ''));
        }
    }, [enabled, showCustomDateField]);

    const handleRadioChange = (val: React.ReactNode) => {
        if (typeof val === 'string') {
            field.onChange(val);

            // Don't set selected key here - let handleOptionKeyChange handle it
            // This is called from RadioOption which knows exactly which option was clicked

            if (showCustomDateField && val && !isCustomDateInOptions(val)) {
                setDisplayedOptions(addCustomDateToOptions(val));
                // Only set custom date option key if it's actually a custom date
            }
        } else {
            field.onChange(null);
            const filteredOptions = options.filter(Boolean).map((option: any, index: number) => ({
                ...option,
                __optionKey: generateOptionKey(index)
            }));

            // Only add custom date option if showCustomDateField is true
            const optionsToDisplay = showCustomDateField
                ? [
                      ...filteredOptions,
                      {
                          label: <Typography variant="labels.largeDemibold" label={t('CUSTOM_DATE_LABEL')} />,
                          value: null,
                          disabled: !enabled,
                          __optionKey: generateOptionKey(filteredOptions.length)
                      }
                  ]
                : filteredOptions;

            setDisplayedOptions(() => optionsToDisplay);
        }
    };

    // Handle option key changes from RadioOption
    const handleOptionKeyChange = (optionKey: string) => {
        setInternalSelectedKey(optionKey);
        setUserHasInteracted(true); // Mark that user has made an explicit selection
        onSelectedKeyChange?.(optionKey); // Notify parent of selected key change

        // If custom date option is selected, set up the custom date (only if showCustomDateField is true)
        if (showCustomDateField) {
            const customDateKey = generateOptionKey(options.filter(Boolean).length);
            if (optionKey === customDateKey) {
                if (!displayedOptions.some((opt: { __optionKey: string }) => opt.__optionKey === customDateKey)) {
                    setDisplayedOptions(addCustomDateToOptions(field.value || ''));
                }
            }
        }
    };
    // If showCustomDateField is false, render only RadioOption component
    if (!showCustomDateField)
        return (
            <RadioOption
                {...field}
                styleOverrides={{}}
                options={displayedOptions}
                onRadioChange={handleRadioChange}
                selectedOptionId={internalSelectedKey}
                onOptionIdChange={handleOptionKeyChange}
            />
        );

    return (
        <>
            <RadioOption
                {...field}
                styleOverrides={{}}
                options={displayedOptions}
                onRadioChange={handleRadioChange}
                selectedOptionId={internalSelectedKey}
                onOptionIdChange={handleOptionKeyChange}
            />{' '}
            {enabled &&
                (!isCustomDateInOptions(field.value) ||
                    internalSelectedKey === generateOptionKey(options.filter(Boolean).length)) && (
                    <BasicDatePicker
                        {...field}
                        enabled={enabled}
                        type="single"
                        startDate={(isMMYYYY ? dayjs().startOf('month') : dayjs().add(1, 'day')).format('MM-DD-YYYY')}
                        value={field.value ? dayjs(field.value, 'DD-MM-YYYY') : null}
                        onChange={(newValue: string | number | Date | dayjs.Dayjs | null | undefined) => {
                            if (dayjs(newValue).year() > 1000) {
                                const picked = isMMYYYY ? dayjs(newValue).startOf('month') : dayjs(newValue);
                                handleRadioChange(picked.format('DD-MM-YYYY'));
                                // Set custom date option as selected when date is picked
                                const customDateKey = generateOptionKey(options.filter(Boolean).length);
                                setInternalSelectedKey(customDateKey);
                                setUserHasInteracted(true);
                                onSelectedKeyChange?.(customDateKey);
                            } else {
                                handleRadioChange(null);
                            }
                            field.onBlur();
                        }}
                        // onBlur={(e: React.ChangeEvent<HTMLInputElement>) => {
                        //     if (dayjs(e.target.value).year() > 1000) {
                        //         handleRadioChange(dayjs(e.target.value).format('DD-MM-YYYY'));
                        //         // Set custom date option as selected when date is entered
                        //         const customDateKey = generateOptionKey(options.filter(Boolean).length);
                        //         setInternalSelectedKey(customDateKey);
                        //         setUserHasInteracted(true);
                        //         onSelectedKeyChange?.(customDateKey);
                        //     } else {
                        //         handleRadioChange(null);
                        //     }
                        // }}
                        data-testid={field.name}
                        isYyyy={isYYYY}
                        isMmYyyy={isMMYYYY}
                        hasError={hasError}
                    />
                )}
        </>
    );
};
